// Self-contained translations for the Historical Cycles Map view.
// Full ES / EN / SK coverage (the broader Liquidity Map module falls
// back to ES for SK by design — this view ships full SK strings so it
// can stand alone on the /liquidity-map/cycles route).
export const CYCLES_T = {
  es: {
    title: "Mapa de Ciclos Históricos",
    subtitle: "Halvings, picos por ciclo y regresión log-power con ±2σ",
    back_to_liqmap: "← Volver al Liquidity Map",
    coin_selector_label: "Activo",
    coin_disabled_tooltip: "No aplica — sin halving",
    loading: "Cargando datos históricos…",
    err_load: "No pudimos cargar los datos. Intenta de nuevo.",
    retry: "Reintentar",
    // Header chips
    source_label: "Fuente: Kraken OHLC semanal",
    updated_just_now: "Actualizado ahora",
    updated_hours_ago: "Actualizado hace {n}h",
    updated_minutes_ago: "Actualizado hace {n}min",
    cached_layer_memory: "memoria",
    cached_layer_mongo: "persistencia",
    cached_layer_live: "en vivo",
    cached_layer_mongo_stale: "persistencia (degradado)",
    // Methodology note (visible, non-hover)
    methodology_title: "Cómo se calcula",
    methodology_body:
      "Techo y suelo de cada ciclo se detectan por cierre semanal (no mecha), entre dos halvings consecutivos. La banda gris es la regresión OLS sobre log10(precio) vs log10(días desde el génesis), con bandas a ±2σ derivadas de la desviación estándar real de los residuos — no un ancho fijo.",
    // Cycle card
    cycle_label: "Ciclo {n}",
    cycle_ongoing: "EN CURSO",
    cycle_peak_close: "Techo de ciclo (cierre semanal)",
    cycle_peak_close_help: "Usamos el cierre semanal (no la mecha intradía) para que el dato sea reproducible entre exchanges. La mecha real suele estar 5–10% por encima — la mostramos aparte como “Máximo histórico conocido”.",
    cycle_known_ath: "Máximo histórico conocido",
    cycle_start_low_label: "Mínimo post-halving",
    cycle_bear_trough_label: "Suelo bear post-techo",
    cycle_no_data: "Sin datos en este rango",
    on_date: "el {date}",
    // Disclaimer (sample warning)
    sample_warning_title: "Muestra estadística reducida",
    sample_warning_low_sample_size:
      "Solo {n} halvings han ocurrido históricamente para este activo. La regresión y la detección de techos/suelos pueden no ser estadísticamente robustas. Trata estos datos como referencia educativa, no como pronóstico.",
    // 4-year-cycle debate note (neutral)
    cycle_debate_title: "Nota sobre el ciclo de 4 años",
    cycle_debate_body:
      "El patrón de ciclos de 4 años está en debate desde que 2025 rompió el patrón histórico por primera vez — se muestra aquí como contexto histórico, no como garantía futura.",
    // Halving countdown (isolated, no price-action framing)
    next_halving_title: "Próximo halving estimado",
    next_halving_days: "~{n} días",
    next_halving_passed: "Ya transcurrido",
    next_halving_note:
      "La fecha exacta varía con el tiempo real de bloque. Dato neutral, sin implicación de movimiento de precio.",
    // Chart legend
    chart_title: "Precio semanal · escala log",
    legend_price: "Precio (cierre)",
    legend_regression: "Regresión log-power",
    legend_band: "Banda ±2σ",
    legend_halving: "Halving",
    legend_peak: "Pico de ciclo",
    legend_trough: "Suelo de ciclo",
    legend_parabolic: "Fase parabólica",
    // Consolidated honesty panel — reuses the strings already in this
    // file for points 1-3; adds two new bullets (4 and 5).
    caveats_panel_title: "Lo que este gráfico NO te dice",
    caveats_panel_intro:
      "Cinco límites honestos antes de interpretar los datos arriba:",
    caveats_band_past_only:
      "La banda ±2σ describe el comportamiento PASADO del precio (dispersión real de los residuos de la regresión). No es una predicción del rango futuro.",
    caveats_parabolic_optional:
      "La detección de fase parabólica usa un criterio estadístico específico (retorno acumulado de 4 semanas > 2× la volatilidad de las 12 semanas previas, sostenido hasta el techo). Puede no aparecer en algún ciclo — y eso NO garantiza que ese patrón se repita en futuros ciclos.",
    // Echo + Drawdown overlay views (event-relative reindex)
    echo_section_title: "Eco de Ciclos · % desde el halving",
    echo_section_subtitle:
      "Cada línea muestra el % de cambio del precio respecto al cierre semanal del halving correspondiente. x=0 es la fecha del halving.",
    drawdown_section_title: "Drawdown desde el techo",
    drawdown_section_subtitle:
      "Cada línea muestra el % desde el techo de su ciclo. x=0 es la semana del techo. Cortada en el suelo de ese ciclo o el siguiente halving, lo que ocurra primero.",
    echo_axis_x: "Semanas desde el halving",
    drawdown_axis_x: "Semanas desde el techo",
    overlay_axis_y_pct: "% de cambio",
    overlay_cycle_label: "Ciclo {n}",
    overlay_cycle_label_ongoing: "Ciclo {n} (en curso)",
    peak_label_intermediate: "Pico intermedio",
    peak_label_final: "Pico final",
    overlay_no_data: "Sin datos suficientes para esta vista.",
    overlay_truncation_note:
      "El ciclo en curso termina en el último cierre semanal real — la línea no se extrapola al futuro.",
    // Share-to-Shadow button
    share_button_label: "📸 Compartir a Shadow",
    share_button_title:
      "Capturar este análisis (chart + transparencia) y publicarlo en Shadow Network",
    share_capture_failed: "No se pudo capturar el análisis",
    // Stats table
    stats_section_title: "Estadísticas históricas por ciclo",
    stats_disclaimer_one:
      "Datos históricos de 1 ciclo completado — muestra insuficiente para proyectar el próximo ciclo. Mostrado como referencia histórica, no como patrón a repetir.",
    stats_disclaimer_many:
      "Datos históricos de los {n} ciclos completados — muestra insuficiente para proyectar el próximo ciclo. Mostrado como referencia histórica, no como patrón a repetir.",
    stats_col_cycle: "Ciclo",
    stats_col_status: "Estado",
    stats_col_duration: "Duración mín→techo",
    stats_col_gain: "Subida mín→techo",
    stats_col_gain_prev_bear: "Subida suelo intermedio→techo",
    stats_col_gain_prev_bear_help: "Solo en picos finales con un pico previo en el mismo ciclo. Mide la subida desde el suelo bear del pico anterior hasta este pico final.",
    stats_col_halving_to_peak: "Halving→techo",
    stats_col_drawdown: "Drawdown techo→suelo bear",
    stats_status_closed: "Cerrado",
    stats_status_ongoing: "En curso",
    stats_value_weeks: "{n} sem",
    stats_value_pct: "{n}%",
    stats_value_dash: "—",
    stats_value_pending: "Pendiente",
    // Entry point — button rendered on /liquidity-map (LiqScoreWidget row)
    entry_button_label: "📈 Ciclos",
    entry_button_title: "Ver Mapa de Ciclos Históricos (halvings + regresión log-power)",
    entry_disabled_tooltip: "No aplica — sin halving",
  },
  en: {
    title: "Historical Cycles Map",
    subtitle: "Halvings, per-cycle peaks and log-power regression with ±2σ",
    back_to_liqmap: "← Back to Liquidity Map",
    coin_selector_label: "Asset",
    coin_disabled_tooltip: "Not applicable — no halving",
    loading: "Loading historical data…",
    err_load: "Could not load data. Please try again.",
    retry: "Retry",
    source_label: "Source: Kraken weekly OHLC",
    updated_just_now: "Updated just now",
    updated_hours_ago: "Updated {n}h ago",
    updated_minutes_ago: "Updated {n}min ago",
    cached_layer_memory: "memory",
    cached_layer_mongo: "persistence",
    cached_layer_live: "live",
    cached_layer_mongo_stale: "persistence (degraded)",
    methodology_title: "How it's computed",
    methodology_body:
      "Each cycle's peak and trough are detected by weekly close (not wick), between two consecutive halvings. The grey band is an OLS regression over log10(price) vs log10(days since genesis), with ±2σ bands derived from the actual standard deviation of residuals — not a fixed width.",
    cycle_label: "Cycle {n}",
    cycle_ongoing: "ONGOING",
    cycle_peak_close: "Cycle top (weekly close)",
    cycle_peak_close_help: "We use the weekly close (not the intraday wick) so the figure is reproducible across exchanges. The real wick is usually 5–10% higher — we display it separately as “Documented all-time high”.",
    cycle_known_ath: "Documented all-time high",
    cycle_start_low_label: "Post-halving low",
    cycle_bear_trough_label: "Post-peak bear bottom",
    cycle_no_data: "No data in this range",
    on_date: "on {date}",
    sample_warning_title: "Limited statistical sample",
    sample_warning_low_sample_size:
      "Only {n} halvings have occurred historically for this asset. The regression and peak/trough detection may not be statistically robust. Treat these data as an educational reference, not a forecast.",
    cycle_debate_title: "Note on the 4-year cycle",
    cycle_debate_body:
      "The 4-year cycle pattern is debated since 2025 broke the historical pattern for the first time — shown here as historical context, not as a future guarantee.",
    next_halving_title: "Next halving (estimated)",
    next_halving_days: "~{n} days",
    next_halving_passed: "Already passed",
    next_halving_note:
      "The exact date varies with real block time. Neutral datum, no implied price movement.",
    chart_title: "Weekly price · log scale",
    legend_price: "Price (close)",
    legend_regression: "Log-power regression",
    legend_band: "±2σ band",
    legend_halving: "Halving",
    legend_peak: "Cycle peak",
    legend_trough: "Cycle trough",
    legend_parabolic: "Parabolic phase",
    caveats_panel_title: "What this chart does NOT tell you",
    caveats_panel_intro:
      "Five honest limits before interpreting the data above:",
    caveats_band_past_only:
      "The ±2σ band describes the PAST behaviour of price (the actual spread of regression residuals). It is NOT a prediction of the future range.",
    caveats_parabolic_optional:
      "The parabolic-phase detection uses a specific statistical criterion (4-week cumulative return > 2× the volatility of the prior 12 weeks, sustained through the peak). It may not appear in some cycles — and that DOES NOT guarantee the pattern will repeat in future cycles.",
    echo_section_title: "Cycle Echo · % since halving",
    echo_section_subtitle:
      "Each line shows the price's % change vs the weekly close of its halving. x=0 is the halving date.",
    drawdown_section_title: "Drawdown from peak",
    drawdown_section_subtitle:
      "Each line shows the % from its cycle's peak. x=0 is the peak week. Cut at the cycle's trough or the next halving, whichever comes first.",
    echo_axis_x: "Weeks since halving",
    drawdown_axis_x: "Weeks since peak",
    overlay_axis_y_pct: "% change",
    overlay_cycle_label: "Cycle {n}",
    overlay_cycle_label_ongoing: "Cycle {n} (ongoing)",
    peak_label_intermediate: "Intermediate peak",
    peak_label_final: "Final peak",
    overlay_no_data: "Not enough data for this view.",
    overlay_truncation_note:
      "The ongoing cycle ends at the most recent real weekly close — the line is NOT extrapolated.",
    share_button_label: "📸 Share to Shadow",
    share_button_title:
      "Capture this analysis (chart + transparency panel) and publish it to Shadow Network",
    share_capture_failed: "Could not capture the analysis",
    stats_section_title: "Per-cycle historical statistics",
    stats_disclaimer_one:
      "Historical data from 1 completed cycle — sample too small to project the next cycle. Shown as historical reference, not as a pattern to repeat.",
    stats_disclaimer_many:
      "Historical data from {n} completed cycles — sample too small to project the next cycle. Shown as historical reference, not as a pattern to repeat.",
    stats_col_cycle: "Cycle",
    stats_col_status: "Status",
    stats_col_duration: "Duration min→peak",
    stats_col_gain: "Gain min→peak",
    stats_col_gain_prev_bear: "Gain prev bear→peak",
    stats_col_gain_prev_bear_help: "Only on final peaks with a prior peak in the same cycle. Measures the rally from the prior peak's bear bottom up to this final peak.",
    stats_col_halving_to_peak: "Halving→peak",
    stats_col_drawdown: "Drawdown peak→bear bottom",
    stats_status_closed: "Closed",
    stats_status_ongoing: "Ongoing",
    stats_value_weeks: "{n} wk",
    stats_value_pct: "{n}%",
    stats_value_dash: "—",
    stats_value_pending: "Pending",
    entry_button_label: "📈 Cycles",
    entry_button_title: "Open the Historical Cycles Map (halvings + log-power regression)",
    entry_disabled_tooltip: "Not applicable — no halving",
  },
  sk: {
    title: "Mapa historických cyklov",
    subtitle: "Halvingy, vrcholy cyklov a log-power regresia s ±2σ",
    back_to_liqmap: "← Späť na Liquidity Map",
    coin_selector_label: "Aktívum",
    coin_disabled_tooltip: "Neaplikovateľné — bez halvingu",
    loading: "Načítavam historické údaje…",
    err_load: "Údaje sa nepodarilo načítať. Skús to znova.",
    retry: "Skúsiť znova",
    source_label: "Zdroj: Kraken týždenné OHLC",
    updated_just_now: "Práve aktualizované",
    updated_hours_ago: "Aktualizované pred {n}h",
    updated_minutes_ago: "Aktualizované pred {n}min",
    cached_layer_memory: "pamäť",
    cached_layer_mongo: "perzistencia",
    cached_layer_live: "naživo",
    cached_layer_mongo_stale: "perzistencia (degradované)",
    methodology_title: "Ako sa to počíta",
    methodology_body:
      "Vrchol a dno každého cyklu sa detegujú podľa týždenného close (nie wick) medzi dvoma po sebe idúcimi halvingmi. Sivý pás je OLS regresia log10(cena) vs log10(dni od genézy), s pásmi ±2σ odvodenými z reálnej smerodajnej odchýlky rezíduí — nie z pevnej šírky.",
    cycle_label: "Cyklus {n}",
    cycle_ongoing: "PREBIEHA",
    cycle_peak_close: "Vrchol cyklu (týždenné close)",
    cycle_peak_close_help: "Používame týždenný close (nie intradenný wick), aby bola hodnota reprodukovateľná medzi burzami. Skutočný wick je zvyčajne 5–10 % vyššie — zobrazujeme ho oddelene ako „Zdokumentované historické maximum“.",
    cycle_known_ath: "Zdokumentované historické maximum",
    cycle_start_low_label: "Minimum po halvingu",
    cycle_bear_trough_label: "Bear dno po vrchole",
    cycle_no_data: "Žiadne údaje v tomto rozsahu",
    on_date: "dňa {date}",
    sample_warning_title: "Obmedzená štatistická vzorka",
    sample_warning_low_sample_size:
      "Pre toto aktívum nastali historicky iba {n} halvingy. Regresia a detekcia vrcholov/dien nemusí byť štatisticky robustná. Tieto údaje považuj za vzdelávaciu referenciu, nie za predpoveď.",
    cycle_debate_title: "Poznámka k 4-ročnému cyklu",
    cycle_debate_body:
      "Vzor 4-ročného cyklu je v diskusii odkedy rok 2025 prvýkrát porušil historický vzor — zobrazené tu ako historický kontext, nie ako budúca garancia.",
    next_halving_title: "Ďalší halving (odhad)",
    next_halving_days: "~{n} dní",
    next_halving_passed: "Už uplynul",
    next_halving_note:
      "Presný dátum sa mení s reálnym časom bloku. Neutrálny údaj, bez implikácie pohybu ceny.",
    chart_title: "Týždenná cena · log mierka",
    legend_price: "Cena (close)",
    legend_regression: "Log-power regresia",
    legend_band: "Pás ±2σ",
    legend_halving: "Halving",
    legend_peak: "Vrchol cyklu",
    legend_trough: "Dno cyklu",
    legend_parabolic: "Parabolická fáza",
    caveats_panel_title: "Čo tento graf NEHOVORÍ",
    caveats_panel_intro:
      "Päť úprimných obmedzení pred interpretáciou údajov vyššie:",
    caveats_band_past_only:
      "Pás ±2σ opisuje MINULÉ správanie ceny (skutočný rozptyl rezíduí regresie). NIE JE to predpoveď budúceho rozsahu.",
    caveats_parabolic_optional:
      "Detekcia parabolickej fázy používa špecifické štatistické kritérium (kumulatívny výnos za 4 týždne > 2× volatilita predchádzajúcich 12 týždňov, udržiavaný až k vrcholu). V niektorých cykloch sa nemusí objaviť — a to NEZARUČUJE, že sa tento vzor zopakuje v budúcich cykloch.",
    echo_section_title: "Echo cyklov · % od halvingu",
    echo_section_subtitle:
      "Každá línia ukazuje % zmenu ceny voči týždennému close halvingu. x=0 je dátum halvingu.",
    drawdown_section_title: "Pokles od vrcholu",
    drawdown_section_subtitle:
      "Každá línia ukazuje % od vrcholu svojho cyklu. x=0 je týždeň vrcholu. Prerušená v dne cyklu alebo pri ďalšom halvingu — podľa toho, čo nastane skôr.",
    echo_axis_x: "Týždne od halvingu",
    drawdown_axis_x: "Týždne od vrcholu",
    overlay_axis_y_pct: "% zmena",
    overlay_cycle_label: "Cyklus {n}",
    overlay_cycle_label_ongoing: "Cyklus {n} (prebieha)",
    peak_label_intermediate: "Medziľahlý vrchol",
    peak_label_final: "Konečný vrchol",
    overlay_no_data: "Nedostatok údajov pre toto zobrazenie.",
    overlay_truncation_note:
      "Prebiehajúci cyklus končí pri poslednom skutočnom týždennom close — línia sa NEEXTRAPOLUJE do budúcnosti.",
    share_button_label: "📸 Zdieľať do Shadow",
    share_button_title:
      "Zachytiť túto analýzu (graf + panel transparentnosti) a zverejniť na Shadow Network",
    share_capture_failed: "Nepodarilo sa zachytiť analýzu",
    stats_section_title: "Historické štatistiky podľa cyklu",
    stats_disclaimer_one:
      "Historické údaje z 1 dokončeného cyklu — vzorka je príliš malá na projektovanie ďalšieho cyklu. Zobrazené ako historická referencia, nie ako vzor na opakovanie.",
    stats_disclaimer_many:
      "Historické údaje z {n} dokončených cyklov — vzorka je príliš malá na projektovanie ďalšieho cyklu. Zobrazené ako historická referencia, nie ako vzor na opakovanie.",
    stats_col_cycle: "Cyklus",
    stats_col_status: "Stav",
    stats_col_duration: "Trvanie min→vrchol",
    stats_col_gain: "Rast min→vrchol",
    stats_col_gain_prev_bear: "Rast pred. bear→vrchol",
    stats_col_gain_prev_bear_help: "Iba pri konečných vrcholoch s predchádzajúcim vrcholom v tom istom cykle. Meria rast od bear dna predchádzajúceho vrcholu po tento konečný vrchol.",
    stats_col_halving_to_peak: "Halving→vrchol",
    stats_col_drawdown: "Pokles vrchol→bear dno",
    stats_status_closed: "Uzavretý",
    stats_status_ongoing: "Prebieha",
    stats_value_weeks: "{n} týž",
    stats_value_pct: "{n}%",
    stats_value_dash: "—",
    stats_value_pending: "Nevyriešené",
    entry_button_label: "📈 Cykly",
    entry_button_title: "Otvoriť mapu historických cyklov (halvingy + log-power regresia)",
    entry_disabled_tooltip: "Neaplikovateľné — bez halvingu",
  },
  "zh-CN": {
    title: "历史周期地图",
    subtitle: "减半、周期顶点及对数幂回归 ±2σ",
    back_to_liqmap: "← 返回流动性地图",
    coin_selector_label: "资产",
    coin_disabled_tooltip: "不适用 — 无减半",
    loading: "正在加载历史数据…",
    err_load: "无法加载数据。请重试。",
    retry: "重试",
    source_label: "数据源: Kraken 周线 OHLC",
    updated_just_now: "刚刚更新",
    updated_hours_ago: "{n}小时前更新",
    updated_minutes_ago: "{n}分钟前更新",
    cached_layer_memory: "内存",
    cached_layer_mongo: "持久化",
    cached_layer_live: "实时",
    cached_layer_mongo_stale: "持久化（已过期）",
    methodology_title: "计算方法",
    methodology_body: "每个周期的顶部和底部通过周收盘价（非影线）检测，时间范围为两个连续减半之间。灰色带是对 log10(价格) 与 log10(创世以来天数) 的 OLS 回归，±2σ 带基于残差的实际标准差推导 — 并非固定宽度。",
    cycle_label: "周期 {n}",
    cycle_ongoing: "进行中",
    cycle_peak_close: "周期顶部（周收盘）",
    cycle_peak_close_help: "我们使用周收盘价（非日内影线）以确保数据在各交易所间可复现。实际影线通常高出 5–10% — 我们将其单独显示为\"已知历史最高价\"。",
    cycle_known_ath: "已知历史最高价",
    cycle_start_low_label: "减半后最低点",
    cycle_bear_trough_label: "顶部后熊市底部",
    cycle_no_data: "此范围内无数据",
    on_date: "于 {date}",
    sample_warning_title: "统计样本有限",
    sample_warning_low_sample_size: "此资产历史上仅发生过 {n} 次减半。回归和顶/底检测可能统计上不够稳健。请将这些数据视为教育参考，而非预测。",
    cycle_debate_title: "关于4年周期的说明",
    cycle_debate_body: "4年周期模式自2025年首次打破历史模式以来一直存在争议 — 此处展示为历史背景，而非未来保证。",
    next_halving_title: "预估下次减半",
    next_halving_days: "约 {n} 天",
    next_halving_passed: "已过去",
    next_halving_note: "具体日期因实际出块时间而异。中性数据，不代表价格走势。",
    chart_title: "周线价格 · 对数刻度",
    legend_price: "价格（收盘）",
    legend_regression: "对数幂回归",
    legend_band: "±2σ 带",
    legend_halving: "减半",
    legend_peak: "周期顶点",
    legend_trough: "周期底部",
    legend_parabolic: "抛物线阶段",
    caveats_panel_title: "此图表无法告诉你的",
    caveats_panel_intro: "在解读上述数据前的五个诚实限制:",
    caveats_band_past_only: "±2σ 带描述价格的历史行为（回归残差的实际离散度）。并非对未来区间的预测。",
    caveats_parabolic_optional: "抛物线阶段检测使用特定统计标准（4周累计回报 > 前12周波动率的2倍，持续至顶点）。某些周期可能不会出现 — 这并不保证该模式会在未来周期重复。",
    echo_section_title: "周期回声 · 自减半起的 %",
    echo_section_subtitle: "每条线显示相对于对应减半的周收盘价的 % 变化。x=0 为减半日期。",
    drawdown_section_title: "自顶部回撤",
    drawdown_section_subtitle: "每条线显示自该周期顶部的 %。x=0 为顶部所在周。截止于该周期底部或下次减半（以先到者为准）。",
    echo_axis_x: "自减半起的周数",
    drawdown_axis_x: "自顶部起的周数",
    overlay_axis_y_pct: "% 变化",
    overlay_cycle_label: "周期 {n}",
    overlay_cycle_label_ongoing: "周期 {n}（进行中）",
    peak_label_intermediate: "中间顶点",
    peak_label_final: "最终顶点",
    overlay_no_data: "此视图无足够数据。",
    overlay_truncation_note: "进行中的周期截止于最后实际周收盘 — 线不会外推至未来。",
    share_button_label: "📸 分享到 Shadow",
    share_button_title: "捕获此分析（图表 + 透明度）并发布到 Shadow Network",
    share_capture_failed: "无法捕获分析",
    stats_section_title: "各周期历史统计",
    stats_disclaimer_one: "历史数据仅1个已完成周期 — 样本不足以预测下个周期。展示为历史参考，而非可重复模式。",
    stats_disclaimer_many: "历史数据包含 {n} 个已完成周期 — 样本不足以预测下个周期。展示为历史参考，而非可重复模式。",
    stats_col_cycle: "周期",
    stats_col_status: "状态",
    stats_col_duration: "最低→顶部时长",
    stats_col_gain: "最低→顶部涨幅",
    stats_col_gain_prev_bear: "中间底部→顶部涨幅",
    stats_col_gain_prev_bear_help: "仅适用于同一周期内有前一顶点的最终顶点。测量从前一顶点的熊市底部到本次最终顶点的涨幅。",
    stats_col_halving_to_peak: "减半→顶部",
    stats_col_drawdown: "顶部→熊市底部回撤",
    stats_status_closed: "已结束",
    stats_status_ongoing: "进行中",
    stats_value_weeks: "{n} 周",
    stats_value_pct: "{n}%",
    stats_value_dash: "—",
    stats_value_pending: "待定",
    entry_button_label: "📈 周期",
    entry_button_title: "查看历史周期地图（减半 + 对数幂回归）",
    entry_disabled_tooltip: "不适用 — 无减半",
  },
  de: {
    title: "Historische Zyklen-Karte",
    subtitle: "Halvings, Zyklusspitzen und Log-Power-Regression mit ±2σ",
    back_to_liqmap: "← Zurück zur Liquidity Map",
    coin_selector_label: "Asset",
    coin_disabled_tooltip: "Nicht anwendbar — kein Halving",
    loading: "Lade historische Daten…",
    err_load: "Daten konnten nicht geladen werden. Bitte erneut versuchen.",
    retry: "Erneut versuchen",
    source_label: "Quelle: Kraken OHLC wöchentlich",
    updated_just_now: "Gerade aktualisiert",
    updated_hours_ago: "Vor {n}h aktualisiert",
    updated_minutes_ago: "Vor {n}min aktualisiert",
    cached_layer_memory: "Speicher",
    cached_layer_mongo: "Persistenz",
    cached_layer_live: "live",
    cached_layer_mongo_stale: "Persistenz (degradiert)",
    methodology_title: "Berechnungsmethode",
    methodology_body: "Zyklus-Top und -Bottom werden durch wöchentlichen Schlusskurs (nicht Docht) zwischen zwei aufeinanderfolgenden Halvings erkannt. Das graue Band ist die OLS-Regression von log10(Preis) vs. log10(Tage seit Genesis), mit Bändern bei ±2σ, abgeleitet von der tatsächlichen Standardabweichung der Residuen — keine feste Breite.",
    cycle_label: "Zyklus {n}",
    cycle_ongoing: "LAUFEND",
    cycle_peak_close: "Zyklus-Top (wöchentlicher Schlusskurs)",
    cycle_peak_close_help: "Wir verwenden den wöchentlichen Schlusskurs (nicht den Intraday-Docht), damit die Daten über Börsen hinweg reproduzierbar sind. Der tatsächliche Docht liegt in der Regel 5–10% höher — wir zeigen ihn separat als \"Bekanntes Allzeithoch\".",
    cycle_known_ath: "Bekanntes Allzeithoch",
    cycle_start_low_label: "Post-Halving-Tief",
    cycle_bear_trough_label: "Bärenmarkt-Boden nach Top",
    cycle_no_data: "Keine Daten in diesem Bereich",
    on_date: "am {date}",
    sample_warning_title: "Reduzierte statistische Stichprobe",
    sample_warning_low_sample_size: "Für dieses Asset haben historisch nur {n} Halvings stattgefunden. Regression und Top/Bottom-Erkennung sind möglicherweise nicht statistisch robust. Behandeln Sie diese Daten als pädagogische Referenz, nicht als Prognose.",
    cycle_debate_title: "Hinweis zum 4-Jahres-Zyklus",
    cycle_debate_body: "Das 4-Jahres-Zyklus-Muster ist umstritten, seit 2025 erstmals das historische Muster durchbrach — es wird hier als historischer Kontext gezeigt, nicht als zukünftige Garantie.",
    next_halving_title: "Geschätztes nächstes Halving",
    next_halving_days: "~{n} Tage",
    next_halving_passed: "Bereits vergangen",
    next_halving_note: "Das genaue Datum variiert mit der tatsächlichen Blockzeit. Neutrale Information ohne Implikation für Preisbewegung.",
    chart_title: "Wochenpreis · Log-Skala",
    legend_price: "Preis (Schlusskurs)",
    legend_regression: "Log-Power-Regression",
    legend_band: "Band ±2σ",
    legend_halving: "Halving",
    legend_peak: "Zyklus-Top",
    legend_trough: "Zyklus-Boden",
    legend_parabolic: "Parabolische Phase",
    caveats_panel_title: "Was dieses Chart NICHT zeigt",
    caveats_panel_intro: "Fünf ehrliche Grenzen vor der Interpretation der obigen Daten:",
    caveats_band_past_only: "Das ±2σ-Band beschreibt das VERGANGENE Preisverhalten (tatsächliche Streuung der Regressions-Residuen). Es ist keine Vorhersage des zukünftigen Bereichs.",
    caveats_parabolic_optional: "Die Erkennung der parabolischen Phase verwendet ein spezifisches statistisches Kriterium (kumulierte 4-Wochen-Rendite > 2× Volatilität der vorherigen 12 Wochen, gehalten bis zum Top). Sie erscheint möglicherweise nicht in jedem Zyklus — und das garantiert NICHT, dass sich dieses Muster in zukünftigen Zyklen wiederholt.",
    echo_section_title: "Zyklen-Echo · % seit dem Halving",
    echo_section_subtitle: "Jede Linie zeigt die %-Änderung des Preises relativ zum wöchentlichen Schlusskurs des entsprechenden Halvings. x=0 ist das Halving-Datum.",
    drawdown_section_title: "Drawdown vom Top",
    drawdown_section_subtitle: "Jede Linie zeigt den % vom Top ihres Zyklus. x=0 ist die Woche des Tops. Endet am Boden dieses Zyklus oder beim nächsten Halving, je nachdem, was zuerst eintritt.",
    echo_axis_x: "Wochen seit Halving",
    drawdown_axis_x: "Wochen seit Top",
    overlay_axis_y_pct: "% Änderung",
    overlay_cycle_label: "Zyklus {n}",
    overlay_cycle_label_ongoing: "Zyklus {n} (laufend)",
    peak_label_intermediate: "Zwischen-Top",
    peak_label_final: "Finales Top",
    overlay_no_data: "Nicht genügend Daten für diese Ansicht.",
    overlay_truncation_note: "Der laufende Zyklus endet beim letzten tatsächlichen wöchentlichen Schlusskurs — die Linie wird nicht in die Zukunft extrapoliert.",
    share_button_label: "📸 Auf Shadow teilen",
    share_button_title: "Diese Analyse (Chart + Transparenz) erfassen und auf Shadow Network veröffentlichen",
    share_capture_failed: "Analyse konnte nicht erfasst werden",
    stats_section_title: "Historische Statistiken pro Zyklus",
    stats_disclaimer_one: "Historische Daten von 1 abgeschlossenem Zyklus — unzureichende Stichprobe zur Projektion des nächsten Zyklus. Als historische Referenz gezeigt, nicht als zu wiederholendes Muster.",
    stats_disclaimer_many: "Historische Daten der {n} abgeschlossenen Zyklen — unzureichende Stichprobe zur Projektion des nächsten Zyklus. Als historische Referenz gezeigt, nicht als zu wiederholendes Muster.",
    stats_col_cycle: "Zyklus",
    stats_col_status: "Status",
    stats_col_duration: "Dauer Tief→Top",
    stats_col_gain: "Anstieg Tief→Top",
    stats_col_gain_prev_bear: "Anstieg Zwischen-Boden→Top",
    stats_col_gain_prev_bear_help: "Nur bei finalen Tops mit vorherigem Top im selben Zyklus. Misst den Anstieg vom Bärenmarkt-Boden des vorherigen Tops bis zu diesem finalen Top.",
    stats_col_halving_to_peak: "Halving→Top",
    stats_col_drawdown: "Drawdown Top→Bärenmarkt-Boden",
    stats_status_closed: "Abgeschlossen",
    stats_status_ongoing: "Laufend",
    stats_value_weeks: "{n} Wo",
    stats_value_pct: "{n}%",
    stats_value_dash: "—",
    stats_value_pending: "Ausstehend",
    entry_button_label: "📈 Zyklen",
    entry_button_title: "Historische Zyklen-Karte anzeigen (Halvings + Log-Power-Regression)",
    entry_disabled_tooltip: "Nicht anwendbar — kein Halving",
  },
};

export const tCycles = (lang) => CYCLES_T[lang] || CYCLES_T.es;
